import React, { useEffect } from "react";
import { Mail, MessageCircle, Instagram, Linkedin, MapPin } from "lucide-react";
import { Reveal, SectionEyebrow } from "@/components/shared/Reveal";
import PulseBriefForm from "@/components/shared/PulseBriefForm";
import { Button } from "@/components/ui/button";
import {
    CONTACT_INFO,
    strategyCallHref,
    strategyCallIsExternal,
    whatsappHref,
} from "@/lib/pulseConstants";
import { trackContactEmail, trackContactWhatsApp } from "@/lib/analytics";

export default function ContactPage() {
    const external = strategyCallIsExternal();
    const bookHref = strategyCallHref();

    useEffect(() => {
        document.title = "Start a Project — REFORM Pulse | Culture-Led Experiential Agency Malaysia";

        // Support deep-link scrolls like /contact#pulse-brief-form
        if (typeof window !== "undefined" && window.location.hash) {
            const id = window.location.hash.slice(1);
            const el = document.getElementById(id);
            if (el) {
                window.setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
            }
        }
    }, []);

    return (
        <div data-testid="contact-page">
            {/* Hero */}
            <section className="relative pt-24 md:pt-28 lg:pt-32 pb-14 overflow-hidden">
                <div className="absolute inset-0 -z-10">
                    <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1478147427282-58a87a120781?auto=format&fit=crop&w=2000&q=80')` }} />
                    <div className="absolute inset-0 hero-radial" />
                    <div className="absolute inset-0 bg-[#07070A]/75" />
                </div>
                <div className="container-editorial py-14">
                    <SectionEyebrow index="//" label="Start A Project" />
                    <Reveal>
                        <h1 className="display-1 text-white text-balance max-w-4xl">
                            Tell us the <span className="text-[#FF2A2A]">moment</span> you want to create.
                        </h1>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="body-lg mt-6 max-w-2xl">
                            Share the shape of the brief. We will review it and come back with the right next step&mdash;usually within 1&ndash;2 working days.
                        </p>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <Button
                                asChild
                                className="cta-glow bg-[#FF2A2A] hover:bg-[#ff4747] text-black font-semibold rounded-full h-12 px-6"
                                data-testid="contact-scroll-to-form"
                            >
                                <a href="#pulse-brief-form" className="inline-flex items-center gap-2">
                                    <Mail size={16} /> Send Us The Brief
                                </a>
                            </Button>
                            {external ? (
                                <Button
                                    asChild
                                    variant="outline"
                                    className="rounded-full border-white/25 bg-transparent hover:bg-white/5 text-white h-12 px-6"
                                    data-testid="contact-book-call-cta"
                                >
                                    <a href={bookHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                                        <MessageCircle size={16} /> Book a Strategy Call
                                    </a>
                                </Button>
                            ) : null}
                            <Button
                                asChild
                                variant="outline"
                                className="rounded-full border-white/25 bg-transparent hover:bg-white/5 text-white h-12 px-6"
                            >
                                <a
                                    href={whatsappHref("Hi REFORM Pulse — I'd like to chat about an event brief.")}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2"
                                    data-testid="contact-whatsapp-cta"
                                    onClick={() => trackContactWhatsApp({ placement: "contact_hero", source_page: "contact" })}
                                >
                                    <MessageCircle size={16} /> WhatsApp Us
                                </a>
                            </Button>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Form + contact */}
            <section className="section-pad" id="pulse-brief-form">
                <span id="pulse-brief" aria-hidden="true" className="absolute -mt-24 block" />
                <div className="container-editorial">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
                        <div className="lg:col-span-4 space-y-8">
                            <div>
                                <div className="eyebrow mb-3">Say hello directly</div>
                                <ul className="space-y-3">
                                    <ContactItem
                                        icon={<Mail size={16} />}
                                        label="Email"
                                        value={CONTACT_INFO.email}
                                        href={`mailto:${CONTACT_INFO.email}`}
                                        testId="contact-email"
                                        onClick={() => trackContactEmail({ placement: "contact_list", source_page: "contact" })}
                                    />
                                    <ContactItem
                                        icon={<MessageCircle size={16} />}
                                        label="WhatsApp"
                                        value={CONTACT_INFO.whatsapp}
                                        href={whatsappHref("Hi REFORM Pulse — I'd like to chat about an event brief.")}
                                        external
                                        testId="contact-whatsapp"
                                        onClick={() => trackContactWhatsApp({ placement: "contact_list", source_page: "contact" })}
                                    />
                                    <ContactItem icon={<Instagram size={16} />} label="Instagram" value={CONTACT_INFO.instagram} href={CONTACT_INFO.instagramUrl} external testId="contact-instagram" />
                                    <ContactItem icon={<Linkedin size={16} />} label="LinkedIn" value={CONTACT_INFO.linkedin} href={CONTACT_INFO.linkedinUrl} external testId="contact-linkedin" />
                                    <ContactItem icon={<MapPin size={16} />} label="Studio" value={CONTACT_INFO.city} />
                                </ul>
                            </div>

                            <div className="rounded-2xl border border-white/10 bg-[#0B0C10] p-5">
                                <div className="font-mono text-xs text-white/60 uppercase tracking-widest mb-2">What happens next</div>
                                <ol className="space-y-3 text-sm text-white/80">
                                    <li className="flex items-start gap-3"><span className="font-mono text-[#FF2A2A]">01</span>We review your brief</li>
                                    <li className="flex items-start gap-3"><span className="font-mono text-[#FF2A2A]">02</span>We come back with the right next step</li>
                                    <li className="flex items-start gap-3"><span className="font-mono text-[#FF2A2A]">03</span>You get a first POV and concept direction</li>
                                </ol>
                            </div>
                        </div>

                        <div className="lg:col-span-8">
                            <PulseBriefForm sourcePage="contact" />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

function ContactItem({ icon, label, value, href, external, testId, onClick }) {
    const commonProps = {
        className: "flex items-center gap-4 p-3 rounded-xl border border-white/10 hover:border-white/25 transition-colors",
        "data-testid": testId,
        ...(onClick ? { onClick } : {}),
    };
    const content = (
        <>
            <span className="h-9 w-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/80">
                {icon}
            </span>
            <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/50">{label}</div>
                <div className="text-sm text-white">{value}</div>
            </div>
        </>
    );
    return (
        <li>
            {href ? (
                <a href={href} {...commonProps} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
                    {content}
                </a>
            ) : (
                <div {...commonProps}>{content}</div>
            )}
        </li>
    );
}
